'use client';

import { useTranslations } from 'next-intl';
import { BaseLineCopy } from '@/app/components/icons/BaseLineCopy';
import { Button } from '@/app/components/ui/button';
import { useCopy } from '@/app/hooks';
import type { TaskInputPrompts } from '@/app/types';
import { cn } from '@/app/utils';

interface MultiFrameTextPromptProps {
  className?: string;
  prompts: Array<TaskInputPrompts>;
}

export const MultiFrameTextPrompt = ({ className, prompts }: MultiFrameTextPromptProps) => {
  const { copy } = useCopy();
  const t = useTranslations('create.img2VideoForm.transition');

  const items = (prompts || [])
    .filter((p) => p.multi_frame)
    .map((p) => ({
      key: p.multi_frame?.range
        ? `${p.multi_frame.range.from}-${p.multi_frame.range.to}`
        : p.multi_frame?.single
          ? `${p.multi_frame.single.index}`
          : p.content,
      label: p.multi_frame?.range
        ? t('label', {
            from: typeof p.multi_frame.range.from === 'number' ? p.multi_frame.range.from + 1 : 1,
            to: typeof p.multi_frame.range.to === 'number' ? p.multi_frame.range.to + 1 : 2,
          })
        : '',
      content: p.content,
      order: p.multi_frame?.range
        ? (p.multi_frame.range.from ?? 0)
        : p.multi_frame?.single
          ? (p.multi_frame.single.index ?? 0)
          : 0,
    }))
    .sort((a, b) => a.order - b.order);

  if (!items.length) return null;

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {items.map((item) => (
        <div key={item.key} className="text-sm text-system-white">
          <div>{item.label}</div>
          <div className="relative mt-1 w-full leading-5">
            <span className="[word-break:break-word]">{item.content}</span>
            <Button size="original" variant="text" className="ml-2 translate-y-0.5">
              <BaseLineCopy
                className="size-4 text-system-white64 hover:text-system-white"
                onClick={() => copy(item.content)}
              />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};
